import { useState } from "react";
import { Heart, Share2, Copy, Check, QrCode, Facebook, ExternalLink, Building2, CreditCard, Smartphone, Gift, Users, Globe, Mail, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

const WEBSITE_URL = "https://pilot-gallery-dev.preview.emergentagent.com";
const EIN = "26-1667883";
const ORG_NAME = "Peace for Change Alliance Inc.";

const donationPlatforms = [
  {
    name: "PayPal Giving Fund",
    description: "Donate through PayPal with 100% going to charity",
    url: `https://www.paypal.com/fundraiser/charity/${EIN}`,
    icon: "💳",
    color: "bg-[#003087]",
    instructions: "Search for 'Peace for Change Alliance' on PayPal Giving Fund"
  },
  {
    name: "Facebook Fundraisers",
    description: "Create a fundraiser or donate on Facebook",
    url: "https://www.facebook.com/fund/PeaceForChangeAlliance/",
    icon: "📘",
    color: "bg-[#1877F2]",
    instructions: "Search for 'Peace for Change Alliance Inc' when creating a Facebook fundraiser"
  },
  {
    name: "GoFundMe Charity",
    description: "Start a fundraiser for Peace for Change Alliance",
    url: `https://www.gofundme.com/sign-up/charity?ein=${EIN}`,
    icon: "💚",
    color: "bg-[#00B964]",
    instructions: "Create a GoFundMe and select Peace for Change Alliance as the beneficiary"
  },
  {
    name: "Network for Good",
    description: "Trusted platform for nonprofit donations",
    url: `https://www.networkforgood.org/donate?ein=${EIN}`,
    icon: "🤝",
    color: "bg-[#E85D04]",
    instructions: "Search using EIN: 26-1667883"
  },
  {
    name: "Benevity",
    description: "Corporate giving and workplace campaigns",
    url: "https://causes.benevity.org/causes/840-261667883",
    icon: "🏢",
    color: "bg-[#6B4EFF]",
    instructions: "For corporate matching gifts, search 'Peace for Change Alliance' in Benevity"
  },
  {
    name: "GuideStar / Candid",
    description: "View our nonprofit profile and transparency",
    url: `https://www.guidestar.org/profile/${EIN}`,
    icon: "⭐",
    color: "bg-[#F7941D]",
    instructions: "View our GuideStar profile for financial transparency"
  },
  {
    name: "Every Action",
    description: "Donate through secure Every Action platform",
    url: `https://secure.everyaction.com/donate?ein=${EIN}`,
    icon: "✨",
    color: "bg-[#00A3E0]",
    instructions: "Search for Peace for Change Alliance"
  },
  {
    name: "JustGiving",
    description: "UK and international donations",
    url: "https://www.justgiving.com/search?q=peace+for+change+alliance",
    icon: "🌍",
    color: "bg-[#AD29B6]",
    instructions: "Search 'Peace for Change Alliance' on JustGiving"
  }
];

const givingMethods = [
  {
    icon: CreditCard,
    title: "Online Donation",
    description: "Give securely through our website with credit card or debit card",
    link: "/donate",
    linkText: "Donate Now"
  },
  {
    icon: Building2,
    title: "Corporate Matching",
    description: "Double your impact through your employer's matching gift program",
    link: "#corporate",
    linkText: "Learn More"
  },
  {
    icon: Gift,
    title: "Planned Giving",
    description: "Include Peace for Change Alliance in your estate planning",
    link: "/contact",
    linkText: "Contact Us"
  },
  {
    icon: Users,
    title: "Peer-to-Peer",
    description: "Start your own fundraiser for birthdays, events, or campaigns",
    link: "#platforms",
    linkText: "Start Fundraiser"
  }
];

export default function WaysToGive() {
  const [copied, setCopied] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);

  const donateUrl = `${WEBSITE_URL}/donate`;
  
  const copyLink = () => {
    navigator.clipboard.writeText(donateUrl);
    setCopied(true);
    toast.success("Donation link copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const shareOnFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(donateUrl)}`, '_blank');
  };

  const shareOnTwitter = () => {
    const text = `Support Peace for Change Alliance in building peace in Milwaukee communities! 🕊️`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(donateUrl)}`, '_blank');
  };

  const shareOnLinkedIn = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(donateUrl)}`, '_blank');
  };

  const shareByEmail = () => {
    const subject = "Support Peace for Change Alliance";
    const body = `I wanted to share this amazing organization with you!\n\nPeace for Change Alliance Inc. is dedicated to building peace in Milwaukee communities through education, advocacy, and community mobilization.\n\nYou can donate here: ${donateUrl}\n\nEIN: ${EIN}`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div data-testid="ways-to-give-page">
      {/* Hero Section */}
      <section className="gradient-hero py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="w-20 h-20 bg-[#F59E0B] rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Heart className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold font-['Outfit'] text-[#0F172A] mb-6">
              Ways to Give
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed mb-4">
              Your generosity helps us build peace in Milwaukee communities. Choose the giving method that works best for you.
            </p>
            <div className="inline-flex items-center gap-2 bg-[#0056D2]/10 px-4 py-2 rounded-full">
              <CheckCircle className="w-5 h-5 text-[#0056D2]" />
              <span className="text-[#0056D2] font-semibold">501(c)(3) Tax-Exempt • EIN: {EIN}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Give Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-[#0056D2] to-[#0044A5] rounded-3xl p-8 md:p-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div className="text-white">
                <h2 className="text-2xl md:text-3xl font-bold font-['Outfit'] mb-4">
                  Quick Donate
                </h2>
                <p className="text-blue-100 mb-6">
                  Make an immediate impact with a secure online donation through our website.
                </p>
                <a href="/donate">
                  <Button className="bg-[#F59E0B] hover:bg-[#D97706] text-white rounded-full px-8 py-4 text-lg font-semibold">
                    <Heart className="w-5 h-5 mr-2" />
                    Donate Now
                  </Button>
                </a>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                <h3 className="text-white font-semibold mb-4">Share Our Cause</h3>
                <div className="flex flex-wrap gap-3 mb-4">
                  <Button onClick={shareOnFacebook} variant="outline" className="border-white/30 text-white hover:bg-white/20">
                    <Facebook className="w-4 h-4 mr-2" />
                    Facebook
                  </Button>
                  <Button onClick={shareOnTwitter} variant="outline" className="border-white/30 text-white hover:bg-white/20">
                    𝕏
                    <span className="ml-2">Twitter</span>
                  </Button>
                  <Button onClick={shareOnLinkedIn} variant="outline" className="border-white/30 text-white hover:bg-white/20">
                    in
                    <span className="ml-2">LinkedIn</span>
                  </Button>
                  <Button onClick={shareByEmail} variant="outline" className="border-white/30 text-white hover:bg-white/20">
                    <Mail className="w-4 h-4 mr-2" />
                    Email
                  </Button>
                </div>
                <div className="flex gap-2">
                  <input 
                    type="text" 
                    value={donateUrl} 
                    readOnly 
                    className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
                  />
                  <Button onClick={copyLink} className="bg-white/20 hover:bg-white/30">
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                  <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-white/20 hover:bg-white/30">
                        <QrCode className="w-4 h-4" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Scan to Donate</DialogTitle>
                      </DialogHeader>
                      <div className="text-center p-6">
                        <img 
                          src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(donateUrl)}`}
                          alt="Donation QR Code"
                          className="mx-auto mb-4"
                        />
                        <p className="text-sm text-slate-600">Scan this QR code to visit our donation page</p>
                        <p className="text-xs text-slate-400 mt-2">Perfect for printed materials and events</p>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Giving Methods */}
      <section className="py-16 bg-[#F0F9FF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold font-['Outfit'] text-[#0F172A] mb-4">
              How You Can Help
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              There are many ways to support our mission of building peace in communities.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {givingMethods.map((method, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-[#0056D2]/10 rounded-xl flex items-center justify-center mb-4">
                  <method.icon className="w-6 h-6 text-[#0056D2]" />
                </div>
                <h3 className="font-semibold text-[#0F172A] text-lg mb-2">{method.title}</h3>
                <p className="text-slate-600 text-sm mb-4">{method.description}</p>
                <a href={method.link} className="text-[#0056D2] font-medium text-sm hover:underline">
                  {method.linkText} →
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Donation Platforms */}
      <section id="platforms" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold font-['Outfit'] text-[#0F172A] mb-4">
              Donate Through Your Favorite Platform
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Find us on these trusted donation platforms. Your donation is tax-deductible.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {donationPlatforms.map((platform, index) => (
              <a 
                key={index}
                href={platform.url}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-lg transition-all group"
              >
                <div className={`w-12 h-12 ${platform.color} rounded-xl flex items-center justify-center mb-4 text-2xl`}>
                  {platform.icon}
                </div>
                <h3 className="font-semibold text-[#0F172A] text-lg mb-2 group-hover:text-[#0056D2] transition-colors">
                  {platform.name}
                </h3>
                <p className="text-slate-600 text-sm mb-3">{platform.description}</p>
                <p className="text-xs text-slate-400">{platform.instructions}</p>
                <div className="mt-4 flex items-center text-[#0056D2] text-sm font-medium">
                  Visit Platform <ExternalLink className="w-4 h-4 ml-1" />
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Corporate Giving */}
      <section id="corporate" className="py-16 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#F59E0B] font-semibold text-sm uppercase tracking-wider">Corporate Giving</span>
              <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] text-white mt-4 mb-6">
                Double Your Impact with Employer Matching
              </h2>
              <p className="text-slate-300 mb-6 leading-relaxed">
                Many employers match charitable donations made by their employees. Check with your HR department to see if your company offers matching gifts.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                  <div>
                    <p className="text-white font-medium">Search for us on Benevity</p>
                    <p className="text-slate-400 text-sm">Many companies use Benevity for workplace giving</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                  <div>
                    <p className="text-white font-medium">Use our EIN: {EIN}</p>
                    <p className="text-slate-400 text-sm">Search using our tax ID on any matching gift platform</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                  <div>
                    <p className="text-white font-medium">Corporate Sponsorship</p>
                    <p className="text-slate-400 text-sm">Contact us for sponsorship opportunities</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
              <h3 className="text-xl font-bold text-white mb-6">Organization Details</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-slate-400 text-sm">Legal Name</p>
                  <p className="text-white font-medium">{ORG_NAME}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Tax ID (EIN)</p>
                  <p className="text-white font-medium font-mono">{EIN}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Tax Status</p>
                  <p className="text-white font-medium">501(c)(3) Public Charity</p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Location</p>
                  <p className="text-white font-medium">Milwaukee, Wisconsin</p>
                </div>
                <div className="pt-4 border-t border-white/10">
                  <Button 
                    onClick={() => {
                      navigator.clipboard.writeText(EIN);
                      toast.success("EIN copied to clipboard!");
                    }}
                    className="w-full bg-[#0056D2] hover:bg-[#0044A5]"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy EIN Number
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stock & Crypto Donations */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold font-['Outfit'] text-[#0F172A] mb-4">
              Other Ways to Give
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-[#F0F9FF] rounded-2xl p-8 text-center">
              <div className="w-16 h-16 bg-[#0056D2] rounded-2xl flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">📈</span>
              </div>
              <h3 className="text-xl font-semibold text-[#0F172A] mb-3">Stock Donations</h3>
              <p className="text-slate-600 mb-4">
                Donate appreciated securities and potentially receive tax benefits while supporting our mission.
              </p>
              <a href="/contact" className="text-[#0056D2] font-medium hover:underline">
                Contact us for details →
              </a>
            </div>

            <div className="bg-[#F0F9FF] rounded-2xl p-8 text-center">
              <div className="w-16 h-16 bg-[#F59E0B] rounded-2xl flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">🪙</span>
              </div>
              <h3 className="text-xl font-semibold text-[#0F172A] mb-3">Cryptocurrency</h3>
              <p className="text-slate-600 mb-4">
                We accept Bitcoin, Ethereum, and other cryptocurrencies through The Giving Block.
              </p>
              <a href="/contact" className="text-[#0056D2] font-medium hover:underline">
                Contact us for details →
              </a>
            </div>

            <div className="bg-[#F0F9FF] rounded-2xl p-8 text-center">
              <div className="w-16 h-16 bg-green-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">📜</span>
              </div>
              <h3 className="text-xl font-semibold text-[#0F172A] mb-3">Legacy Giving</h3>
              <p className="text-slate-600 mb-4">
                Include Peace for Change Alliance in your will or estate plan to create lasting impact.
              </p>
              <a href="/contact" className="text-[#0056D2] font-medium hover:underline">
                Contact us for details →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-[#F0F9FF]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold font-['Outfit'] text-[#0F172A] mb-4">
            Questions About Giving?
          </h2>
          <p className="text-slate-600 mb-8">
            Our team is here to help you find the best way to support our mission.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="/contact">
              <Button className="btn-primary">
                <Mail className="w-5 h-5 mr-2" />
                Contact Us
              </Button>
            </a>
            <a href="tel:414-502-7296">
              <Button variant="outline" className="btn-secondary">
                <Smartphone className="w-5 h-5 mr-2" />
                Call 414-502-7296
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
